import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import VideoBackground from '../../../components/base/VideoBackground';
import FloatingSocialIcons from '../../../components/base/FloatingSocialIcons';

const BusinessListingsPage: React.FC = () => {
  const benefits = [
    {
      icon: 'ri-map-pin-line',
      title: 'Local Visibility',
      description: 'Get found by customers in your area when they search for your services online.'
    },
    {
      icon: 'ri-star-line',
      title: 'Build Trust',
      description: 'Consistent business information across platforms builds credibility with customers.'
    },
    {
      icon: 'ri-phone-line',
      title: 'More Calls',
      description: 'Accurate contact information leads to more phone calls and walk-in customers.'
    },
    {
      icon: 'ri-trending-up-line',
      title: 'Increase Revenue',
      description: 'Better online visibility directly translates to more customers and higher sales.'
    }
  ];

  const features = [
    'Google My Business optimization',
    'Yelp business profile setup',
    'Facebook business page',
    'Apple Maps listing',
    'Bing Places submission',
    'Industry-specific directories',
    'NAP consistency check',
    'Review monitoring setup'
  ];

  return (
    <div className="min-h-screen bg-white relative overflow-hidden">
      <VideoBackground />
      <FloatingSocialIcons />
      <Header />
      
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4">
        <div className="absolute inset-0 z-0">
          <img
            src="https://static.readdy.ai/image/cfeaaf915a66e3490d7a0ef017ccbecc/0e226a3206759fb18d3a3214fe75d73f.jpeg"
            alt="Background"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-white/95 via-white/90 to-white/95"></div>
        </div>
        
        <div className="max-w-6xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center bg-gradient-to-r from-green-100 to-blue-100 rounded-full px-6 py-2 mb-6">
            <i className="ri-building-line text-green-600 mr-2"></i>
            <span className="text-green-800 font-medium">Business Listings Management</span>
          </div>
          
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            <span className="bg-gradient-to-r from-green-600 via-blue-600 to-purple-600 bg-clip-text text-transparent">
              Get Found Online
            </span>
            <br />
            By Local Customers
          </h1>
          
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            Maximize your local visibility with professional business listing management across all major platforms and directories.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/consultation"
              className="bg-gradient-to-r from-green-600 to-blue-600 text-white px-8 py-4 rounded-full font-semibold hover:shadow-lg transform hover:scale-105 transition-all duration-300 whitespace-nowrap"
            >
              <i className="ri-calendar-line mr-2"></i>
              Book Free Consultation
            </Link>
            <Link
              to="/quote"
              className="bg-white text-blue-600 border-2 border-blue-600 px-8 py-4 rounded-full font-semibold hover:bg-blue-50 transition-all duration-300 whitespace-nowrap"
            >
              <i className="ri-file-text-line mr-2"></i>
              Request Free Quote
            </Link>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-green-50 to-blue-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">
              <span className="bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent">
                Why Business Listings Matter for Small Businesses
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              78% of local mobile searches result in offline purchases. Make sure your business is found when customers are ready to buy.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2">
                <div className="w-16 h-16 bg-gradient-to-r from-green-500 to-blue-500 rounded-2xl flex items-center justify-center mb-6">
                  <i className={`${benefit.icon} text-2xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold mb-4 text-gray-800">{benefit.title}</h3>
                <p className="text-gray-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl font-bold mb-6">
                <span className="bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent">
                  Complete Listing Management
                </span>
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                We handle everything from initial setup to ongoing maintenance, ensuring your business information is always accurate and up-to-date.
              </p>
              
              <div className="grid sm:grid-cols-2 gap-4">
                {features.map((feature, index) => (
                  <div key={index} className="flex items-center">
                    <div className="w-6 h-6 bg-gradient-to-r from-green-400 to-blue-500 rounded-full flex items-center justify-center mr-3">
                      <i className="ri-check-line text-white text-sm"></i>
                    </div>
                    <span className="text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="relative">
              <img
                src="https://public.readdy.ai/ai/img_res/7730ff5df9dcb7e5d2c46e2b09552cef.jpg"
                alt="Business Listings Management"
                className="rounded-2xl shadow-2xl object-cover w-full h-96"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-green-600 via-blue-600 to-purple-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Dominate Local Search?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Let us optimize your business listings and help local customers find you easily.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <Link
              to="/consultation"
              className="bg-white text-blue-600 px-8 py-4 rounded-full font-semibold hover:shadow-lg transform hover:scale-105 transition-all duration-300 whitespace-nowrap"
            >
              <i className="ri-calendar-line mr-2"></i>
              Book Free Consultation
            </Link>
            <Link
              to="/quote"
              className="bg-transparent text-white border-2 border-white px-8 py-4 rounded-full font-semibold hover:bg-white hover:text-blue-600 transition-all duration-300 whitespace-nowrap"
            >
              <i className="ri-file-text-line mr-2"></i>
              Request Free Quote
            </Link>
          </div>

          {/* Social Media Links */}
          <div className="flex justify-center space-x-6">
            <a href="#" className="text-white hover:text-green-200 transition-colors duration-300">
              <i className="ri-facebook-fill text-2xl"></i>
            </a>
            <a href="#" className="text-white hover:text-green-200 transition-colors duration-300">
              <i className="ri-instagram-fill text-2xl"></i>
            </a>
            <a href="#" className="text-white hover:text-green-200 transition-colors duration-300">
              <i className="ri-twitter-fill text-2xl"></i>
            </a>
            <a href="#" className="text-white hover:text-green-200 transition-colors duration-300">
              <i className="ri-linkedin-fill text-2xl"></i>
            </a>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default BusinessListingsPage;
